import { useMediaQuery, Drawer, Card, CardContent, CardHeader, Grid, Button, Stack, Autocomplete, TextField, Box, FormHelperText, Typography, Link, Alert } from "@mui/material";
import { Helmet } from 'react-helmet-async';
import { useRecordContext } from "ra-core";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ClientManageDto } from "@/gen/api/user";
import { ClientSettingDto } from "@/gen/api/securefile";
import ReadonlyField from "@/components/input/ReadonlyField";
import WithSave from "@/controller/WithSave";
import { isValidPathFormat, isValidPathFormatUnix } from "./EditClientScanFolders";
import Confirm from "@/components/widgets/Confirm";



const EditDMRescan: FC<{
  open: boolean;
  onClose: () => void;
  fallbackOptions: string[];
}> = ({ 
  open, onClose, fallbackOptions,
}) => {
  const match = useMediaQuery('(max-width: 680px)')
  const width = match ? 'calc(100vw - 50px)' : 630
  const [client, originSetting] = useRecordContext() as any as [ClientManageDto, ClientSettingDto] ?? [null, null]

  const {isMac, rootFolders} = useMemo(() => {
    const isMac = client?.osInfo?.toLowerCase().startsWith("unix")
    const rootFolders = (originSetting?.scanRootFolders && originSetting.scanRootFolders.length > 0 
      ? originSetting.scanRootFolders! 
      : fallbackOptions
    ).map(s => {
      const c = s[s.length - 1]
      // ensure not trailing path seperator at end
      return c === '/' || c === '\\' ? s.slice(0, s.length - 1) : s
    })
    return {isMac, rootFolders}  
  }, [client?.osInfo, fallbackOptions, originSetting?.scanRootFolders])

  const [states, setStates] = useState({
    selectedRootFolders: [] as string[],
    error: null as string|null,
    succeededOnce: false,
    toConfirmReplace: null as {parent:string, children:string[], directionRpwc:boolean}|null,
    transientHint: null as {message:string, at:number}|null
  })

  const setTransientHint = (message: string, v0?: typeof states) => {
    const ops = (v: typeof states) => {
      if (v.transientHint) {
        window.clearTimeout(v.transientHint.at)
      }
      const transientHint = {
        message,
        at: window.setTimeout(() => {
          setStates(v1 => ({...v1, transientHint: null}))
        }, 5000)
      }
      return {...v, transientHint}
    }
    if (v0) {
      return ops(v0)
    } else {
      setStates(v => ops(v))
    }
  }
  const inputRef = useRef<HTMLInputElement>()
  const prefillInput = (value: string) => {
    if (inputRef.current && value) {
      const inputEl = inputRef.current
      inputEl.value = value
      inputEl.setSelectionRange(value.length, value.length+1)
    }
  }

  const handleSelect = (value: string[]) => {
    setStates(v => {
      const selected: string = value[value.length - 1]  
      const selectedLower = selected.toLowerCase()
      const children = v.selectedRootFolders.filter(s => {
        const sLower = s.toLowerCase()
        return sLower.startsWith(selectedLower)
      })
      if (children.length > 0) {
        return {...v, toConfirmReplace: {parent: selected, children, directionRpwc: false}}
      }
      return ({...v, selectedRootFolders: value})
    })
  }
  const handleCreate = (value: string[]) => {
    const created: string = inputRef.current!.value.trim().replace(/[\\/]$/, '')
    const isValid = isMac ? isValidPathFormatUnix : isValidPathFormat
    if (!isValid(created)) { 
      setTransientHint(`"${created}" is not a valid path`)  
      return 
    }
    const isParentSubfolder = (p: string, s: string) => {
      if (!s.startsWith(p) || s.length < p.length + 2) { return false }
      const c = s[p.length]
      return c === '/' || c === '\\'
    }
    const isSubfolderOfAnyRoot = rootFolders.some(r => isParentSubfolder(r, created))
    if (isSubfolderOfAnyRoot) {
      setStates(v => {
        const createdLower = created.toLowerCase()
        const i = v.selectedRootFolders.findIndex(s => s.toLowerCase() === createdLower)
        if (i > -1) {
          v.selectedRootFolders.splice(i, 1)
          return setTransientHint(`Folder "${created}" is removed`, {...v})!
        }
        const {parent, children} = v.selectedRootFolders.reduce((acc, s) => {
          const sLower = s.toLowerCase()
          if (isParentSubfolder(sLower, createdLower)) {
            acc['parent'] = s
          } else if (isParentSubfolder(createdLower, sLower)) {
            const ls = acc['children'] ?? (acc['children'] = [])
            ls.push(s)
          }
          return acc
        }, {} as any)
        if (parent) {
          return {...v, toConfirmReplace: {parent, children: [created], directionRpwc: true}}
        }
        if (children && children.length > 0) {
          return {...v, toConfirmReplace: {parent: created, children, directionRpwc: false}}
        }
        return ({...v, selectedRootFolders: value})
      })
    } else {
      setTransientHint('Should select a Root Folder, or enter a subfolder of Root Folder')
    }
  }
  const handleReplace = () => {
    setStates(v => {
      if (!v.toConfirmReplace) { return v }
      const b = v.toConfirmReplace
      const [replaced, replacement] = 
        b.directionRpwc
        ? [[b.parent], b.children]
        : [b.children, [b.parent]]
      const sReplaced = new Set(replaced)
      const selectedRootFolders = [...v.selectedRootFolders.filter(s => !sReplaced.has(s)), ...replacement]
      return {...v, selectedRootFolders, toConfirmReplace: null}
    })
  }

  return (
    <>
      {open && 
        <Helmet>
          <title>DataManager Rescan | OnData</title>
        </Helmet>}
      <Drawer anchor="right" PaperProps={{ sx: { p: 2, width, }}}
        onClose={onClose}
        open={open}
      >
        <Card>
          <CardHeader title="DataManager Rescan" />
          <CardContent>
            <Grid container spacing={4} sx={{ minHeight: 100, maxHeight: 'calc(100vh - 160px)', overflow: "auto" }}>
              <Grid item xs={12}>
                <ReadonlyField label="Client Name" name="ClientName" value={client?.clientName ?? ''} />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  After a rescan is scheduled, all files within the designated folder(s) will be rescanned during next round of auto discovery, which could take a considerable amount of time, depending on the number of files to be rescanned.
                </Alert>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Please be aware of the Scheduled Rescan, when received by the client, will override and cancel any ongoing scan job that was active on that client, including a previous Scheduled Rescan.
                </Alert>
                <Stack direction="row" justifyContent="space-between">
                  <FormHelperText sx={{ pl: 1.5 }}>Rescan Root Folders</FormHelperText>
                  <Button variant="text" size="small"
                    disabled={states.succeededOnce}
                    onClick={() => setStates(v => ({...v, selectedRootFolders: [...rootFolders]}))}
                  >
                    Load All Root Folders
                  </Button>
                </Stack>
                <Box sx={{ 
                  overflowX: 'hidden',
                  '& div.MuiOutlinedInput-root': {
                    flexDirection: 'column',
                    alignItems: 'start',
                    '& > div.MuiChip-root': {
                      height: 'auto', minHeight: 32,
                      '& > span.MuiChip-label': {
                        display: 'block',
                        wordBreak: 'break-all',
                        whiteSpace: 'break-spaces',
                        color: '#ad5324',
                      },
                    },
                    '& > input': { flex: '100%', width: '100% !important' },
                  },
                }}>
                  <Autocomplete multiple freeSolo
                    disabled={states.succeededOnce}
                    options={rootFolders}
                    getOptionLabel={(option: any) => option}
                    value={states.selectedRootFolders}
                    renderInput={(params:any) => {
                      return (
                        <TextField {...params} inputRef={inputRef} placeholder="Choose Root Folders to rescan" />
                      )
                    }}
                    onChange={(e:any, value, reason, details) => { 
                      if (reason === 'removeOption' || reason === 'clear') {
                        setStates(v => ({...v, selectedRootFolders: value}))
                      } else if (reason === 'selectOption') {
                        handleSelect(value)
                      } else if (reason === 'createOption') { // freeSolo input
                        handleCreate(value)
                      }
                    }}
                    onHighlightChange={(e, option) => {
                      const highlightedRootFolder: string = option
                      prefillInput(highlightedRootFolder)
                    }}
                  />
                  {states.transientHint &&
                    <FormHelperText sx={{ color: 'warning.light', ml: 1.5 }}>{states.transientHint?.message}</FormHelperText>}
                </Box>
              </Grid>
            </Grid>
            {states.error && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {states.error}
                </FormHelperText>
              </Box>)}
          </CardContent>
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
            <Button color="primary" type="button" variant="outlined"
              onClick={onClose}
            >
              Cancel
            </Button>
            <WithSave render={({ save, saving}) =>
              <>
                {!states.succeededOnce 
                ? <Button color="primary" type="submit" variant="contained" 
                    disabled={states.selectedRootFolders.length === 0 || saving}
                    onClick={() => {
                      const payload = { clientId: originSetting.clientId, scanRootFolders: states.selectedRootFolders }
                      save(payload, {
                        onSuccess: () => {
                          setStates(v => ({...v, error: null, succeededOnce: true }))
                          // onClose()
                        },
                        onFailure: (err:any) => {
                          setStates(v => ({...v, error: err.message }))
                        }
                      })
                    }}
                  >
                    Schedule Rescan
                  </Button>
                : <Box sx={{ alignSelf: 'center', '& a.MuiLink-root': {cursor: "pointer"} }}>
                    <Typography variant="caption">
                      Rescan scheduled, {" "}
                      <Link onClick={() => setStates(v => ({...v, succeededOnce: false }))} >
                        schedule again?
                      </Link>
                    </Typography>
                  </Box>  
                }
              </>
            } />
          </Stack>
        </Card>
        <Confirm
          isOpen={Boolean(states.toConfirmReplace)}
          title={"Confirm Replace Folder(s)"}
          loading={false}
          onConfirm={handleReplace}
          onClose={() => setStates(v => v.toConfirmReplace ? {...v, toConfirmReplace: null} : v)}
          confirm="Confirm"
          cancel="Cancel"
          content={((() => {
            if (!states.toConfirmReplace) { return null }
            const {parent, children, directionRpwc} = states.toConfirmReplace

            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
                  <Box>
                    <Typography variant="body2" sx={{ ml: 1, wordBreak: 'break-all' }}>
                      The folder {<b>{` "${parent}" `}</b>} <br/>you {directionRpwc ? "had added previously" : "are about to add"} contains the following {children.length > 1 ? "subfolders" : "subfolder"}. 
                    </Typography>
                    <Box 
                      sx={{ mt: 2, px: 3, py: 1,
                        borderRadius: '8px', 
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: 150,
                        overflow: 'auto',
                        bgcolor: theme => theme.palette.background.default 
                      }}
                    >
                      {children.map((t, i) => (
                        <Typography key={i} component="li" variant="caption">
                          <b>{t}</b>
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ ml: 1, mt: 2 }} >
                      {directionRpwc 
                        ? "It shall be replaced by the more specific subfolder."
                        : "It shall replace all the listed subfolders."}
                    </Typography>
                  </Box>
                </Box>
              </Box>)
          })())}
        />
      </Drawer>
    </>
  )
}

export default EditDMRescan;



